/**
 * The **volley FX planner** (arena fire-mage rework, ADR-0181): the pure,
 * Pixi-free bridge between one owner-only `ArenaFx` message (the sim's
 * depth-first {@link ArenaFxEvent} cascade, ADR-0179) and the sprite/primitive
 * effects the {@link ./arenaFx.ArenaFxOverlay} plays. The sim resolved the
 * whole volley in one tick; this module turns that instant cascade back into
 * a *staged show* - staggered projectile launches, impacts landing a flight
 * later, chains leaping only after their parent lands - as a flat list of
 * timed ops the renderer schedules verbatim.
 *
 * Pure and clock-free (the `stepAnimator.ts` discipline): positions come from
 * an injected resolver (the renderer prefers a live sprite and falls back to
 * the event's resolution-time tile, so a despawned enemy still anchors its
 * FX), and all timing derives from the named constants below. Node-testable
 * with a fixed resolver.
 *
 * **The trigger rule:** the sim emits events depth-first, so an
 * explosion/flame-strike/chain event's trigger is always the nearest
 * *preceding* flight event (projectile or chain) in the array - it fires at
 * that flight's impact instant. Root projectiles launch staggered by volley
 * index; a leading non-flight event (impossible today, but the wire is
 * server-authored) degrades to time zero.
 */
import { TILE_HALF } from '../sim/projection';
import { bloodArt } from '../sim/content';
import type { ArenaFxEvent, ArenaFxTile } from '../sim/arena';
import type { ArenaProjectileKind } from '../sim/arenaWeapons';
import type { EntityId } from '../sim/entity';
import type { GroundEffectId, ImpactEffectId, ProjectileEffectId } from '../sim/content';
import { arenaFxPriority, type ArenaFeedbackPriority } from '../performance/arenaPresentationBudget';

/** A world-local point (pre-camera pixels - the space the FX overlay draws in). */
export interface VolleyPoint {
  x: number;
  y: number;
}

/** Projectile flight speed, in tiles per second (a tile is 2*TILE_HALF px across in x). */
export const PROJECTILE_TILES_PER_S = 10;

/** Derived flight speed in world-local px/s. */
export const PROJECTILE_SPEED_PX_PER_S = PROJECTILE_TILES_PER_S * TILE_HALF * 2;

/** Launch stagger between one volley's root projectiles, ms - the fan reads as a fan. */
export const VOLLEY_STAGGER_MS = 80;

/** Launch stagger between a nova ring's successive rays, ms (PRD #378): the
 * ring reads as a rotation - a spiral - rather than one simultaneous flash. */
export const NOVA_RAY_STAGGER_MS = 40;

/** Base Nova's visual spokes: enough simultaneous fireballs to read as a
 * circle while leaving the sim's continuous circular hit area authoritative. */
export const NOVA_FIREBALL_COUNT = 16;

/** How long the meteor's star visibly falls before detonating, ms (PRD #378).
 * The sim already applied the damage at the impact tick; the fall is show. */
export const METEOR_FALL_MS = 350;

/** How far above the impact tile the star spawns, world-local px. */
export const METEOR_DROP_PX = 260;

/** How long a Comet-shoved enemy slides from its strike tile to `knockedTo`,
 * ms (PRD #419 slice E): quick enough to read as a hurl, not a walk. */
export const COMET_HURL_MS = 180;

/** Floor on a flight so a point-blank shot still visibly flies. */
export const PROJECTILE_MIN_FLIGHT_MS = 90;

/** Cap on a flight so a cross-map shot never lingers for seconds. */
export const PROJECTILE_MAX_FLIGHT_MS = 900;

/** One flight's duration from its endpoints (clamped; shared by planner + overlay). */
export function projectileFlightMs(from: VolleyPoint, to: VolleyPoint): number {
  const dist = Math.hypot(to.x - from.x, to.y - from.y);
  return Math.min(
    PROJECTILE_MAX_FLIGHT_MS,
    Math.max(PROJECTILE_MIN_FLIGHT_MS, (dist / PROJECTILE_SPEED_PX_PER_S) * 1000),
  );
}

/** One timed FX op: everything the overlay/renderer needs, no event left to interpret. */
export type PlannedFx =
  /** A projectile flight (root or chain hop) launching at `atMs`. */
  | {
      atMs: number;
      op: 'flight';
      priority: ArenaFeedbackPriority;
      from: VolleyPoint;
      to: VolleyPoint;
      effect: ProjectileEffectId;
      sizeTier: number;
      flightMs: number;
      /**
       * The single enemy this flight locks, when it homes onto one (a plain
       * projectile / chain hop). The renderer re-resolves this entity's live
       * position each frame so the tracer lands ON the target even as it moves
       * (ADR-0216). Absent on a pierced serpent / nova ray, which aim at a fixed
       * ray-end point, not one mover.
       */
      target?: EntityId;
    }
  /** A one-shot impact anim (explosion / flame strike) at `atMs`. */
  | {
      atMs: number;
      op: 'impact';
      priority: ArenaFeedbackPriority;
      at: VolleyPoint;
      effect: ImpactEffectId;
      radiusTiles: number;
    }
  /** A damage float over a struck entity at `atMs` (its hit's landing instant).
   * `viaEffect` names the projectile that struck when it should sound a physical
   * hit (the archer's arrow/bolt thwack, ADR-0237); absent for splash/magic hits. */
  | {
      atMs: number;
      op: 'float';
      target: EntityId;
      damage: number;
      priority: ArenaFeedbackPriority;
      viaEffect?: ProjectileEffectId;
      ground?: boolean;
    }
  /** A looping ground effect (fire field / sparkle telegraph) held for `durationMs` (PRD #378).
   * A `molten` effect (PRD #419 slice B) also carries `art` - the deterministic
   * blood-static pick the splat draws (tinted {@link MOLTEN_TINT}); strip/sparkle
   * ops never do. */
  | {
      atMs: number;
      op: 'ground';
      priority: ArenaFeedbackPriority;
      at: VolleyPoint;
      effect: GroundEffectId;
      durationMs: number;
      art?: number;
    }
  /** A Comet knockback hurl (PRD #419 slice E): at `atMs` (the projectile's
   * impact instant) the struck enemy's sprite slides `fromTile` -> `toTile`
   * over `durationMs`. Carried in TILE space, not px: the renderer drives the
   * enemy's own step animator (which replays in tiles), never a detached FX
   * sprite - the enemy itself is what flies. */
  | {
      atMs: number;
      op: 'hurl';
      priority: ArenaFeedbackPriority;
      target: EntityId;
      fromTile: ArenaFxTile;
      toTile: ArenaFxTile;
      durationMs: number;
    }
  /** A batched **status** tick marker cascade (archer, PRD #446 Slice 1,
   * ADR-0237): tiny cosmetic blips at each `marks` point (a blood spurt over a
   * bleeding enemy). Purely visual - the DoT damage already landed sim-side. */
  | {
      atMs: number;
      op: 'status';
      priority: ArenaFeedbackPriority;
      marks: { at: VolleyPoint; status: 'bleed' | 'poison' | 'slow' }[];
    };

/**
 * Resolve an event anchor to a world-local point: prefer the live entity's
 * sprite when the renderer still has one, else project the resolution-time
 * tile. Never fails - the tile always projects.
 */
export type VolleyResolver = (tile: ArenaFxTile, entity?: EntityId) => VolleyPoint;

/** The wire projectile look -> the baked effect id it plays. The archer's
 * physical `bow-arrow`/`bolt` (PRD #446 Slice 1, ADR-0237) map to their own
 * item-art strips, distinct from the mage's magic `arrow` (which is the glowing
 * `magic-arrow` puff). */
function projectileEffect(kind: ArenaProjectileKind): ProjectileEffectId {
  switch (kind) {
    case 'fire-snake':
      return 'fire-snake';
    case 'fireball':
      return 'fireball';
    case 'bow-arrow':
      return 'arrow';
    case 'bolt':
      return 'bolt';
    default:
      return 'magic-arrow';
  }
}

/** How far along the `from`->`to` flight a point sits, clamped 0..1 (pierce float timing). */
function rayFraction(from: VolleyPoint, to: VolleyPoint, point: VolleyPoint): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return 1;
  const t = ((point.x - from.x) * dx + (point.y - from.y) * dy) / lenSq;
  return Math.min(1, Math.max(0, t));
}

/**
 * The fire-field strip a burning tile loops. A Scorched Earth trail event
 * carries the run line's `orient` (PRD #419: the sim derived it from the step
 * delta), which wins outright - the flames lie along the trail. Without one
 * (a Cataclysm ground patch has no direction), orientation falls back to tile
 * parity: a multi-tile patch mixes the east-west and north-south strips
 * checkerboard fashion, which reads as organic flame rather than a repeated
 * stamp. Deterministic either way.
 */
export function fireFieldEffect(tile: ArenaFxTile, orient?: 'ew' | 'ns'): GroundEffectId {
  if (orient !== undefined) return orient === 'ew' ? 'fire-field-ew' : 'fire-field-ns';
  return (tile.x + tile.y) % 2 === 0 ? 'fire-field-ew' : 'fire-field-ns';
}

/** The poison-field strip a Poison Arrows cloud tile loops (archer, ADR-0237) -
 * the fire-field ns/ew picker, in the poison palette. */
export function poisonFieldEffect(tile: ArenaFxTile, orient?: 'ew' | 'ns'): GroundEffectId {
  if (orient !== undefined) return orient === 'ew' ? 'poison-field-ew' : 'poison-field-ns';
  return (tile.x + tile.y) % 2 === 0 ? 'poison-field-ew' : 'poison-field-ns';
}

/** The fire tint the molten residue splats *fall back* to over the red blood art
 * (PRD #419 slice B) when no blaze hue ramp is bound - a runtime Pixi tint. The
 * shipped look now recolours the splat with the real UO blaze hue (1161) via
 * the shared UO hue sprite batch (see `arenaFx.setMoltenHue` / `gameRenderer.BLAZE_HUE`):
 * a flat tint only *multiplies*, so it could never lift the red art to fire - it
 * read as "still just blood" - whereas the hue ramp remaps it like `bodyHue`.
 * Kept for the degraded unbaked-hue path and the fallback splat blob below. */
export const MOLTEN_TINT = 0xff8030;

/** How many blood-splat statics the molten look can pick from (the `bloodArt`
 * registry's art-index range 0..N-1, the PRD #372 wire convention). */
export const MOLTEN_SPLAT_COUNT = bloodArt.all().length;

/**
 * The blood-static art index a molten tile splats with (PRD #419 slice B): a
 * pure per-tile hash - deterministic so a replayed run (and a re-rendered
 * frame) always shows the identical residue, varied across tiles so a pool
 * never reads as one repeated stamp. No rng on the render path, ever.
 */
export function moltenSplatArt(tile: ArenaFxTile): number {
  // Two large odd primes decorrelate the axes (the classic spatial-hash mix).
  const h = (Math.imul(tile.x, 73856093) ^ Math.imul(tile.y, 19349663)) >>> 0;
  return h % MOLTEN_SPLAT_COUNT;
}

/**
 * Turn one volley's event cascade into a flat timed plan (see the module
 * header for the trigger rule). Damage floats land at their hit's impact
 * instant - a projectile/chain floats over its target, an explosion/flame
 * strike over each splashed victim - so the numbers appear as the show lands,
 * not all at once on receipt.
 */
export function planVolleyFx(events: readonly ArenaFxEvent[], resolve: VolleyResolver): PlannedFx[] {
  const plan: PlannedFx[] = [];
  let rootIndex = 0; // gen-0 projectiles launch staggered by their volley position
  let lastImpactAt = 0; // the nearest preceding flight's landing instant
  // The nearest preceding ROOT projectile's landing instant: a splinter burst
  // (PRD #419 slice D) radiates from its PARENT projectile's struck tile at the
  // parent's own impact, even though the sim appends the burst after the whole
  // parent cascade (chain hops included) - `lastImpactAt` would by then be a
  // distant hop's landing, not the tile the burst visibly erupts from.
  let lastRootImpactAt = 0;

  for (const event of events) {
    const priority = arenaFxPriority(event);
    switch (event.kind) {
      case 'projectile':
      case 'chain': {
        // A piercing serpent's `to` is the ray's END tile; the aimed target is
        // struck partway along, so its float (and the cascade hanging off it)
        // lands at the target's own point on the ray, not the flight's end.
        const from = resolve(event.from, event.kind === 'chain' ? event.fromEnemy : undefined);
        const to = resolve(event.to, event.kind === 'projectile' && event.pierced ? undefined : event.target);
        const launchAt = event.kind === 'projectile' ? rootIndex++ * VOLLEY_STAGGER_MS : lastImpactAt;
        const flightMs = projectileFlightMs(from, to);
        if (event.kind === 'projectile' && event.pierced) {
          const targetAt = resolve(event.to, event.target);
          const strike = launchAt + flightMs * rayFraction(from, to, targetAt);
          lastImpactAt = strike;
          lastRootImpactAt = strike;
          plan.push({
            atMs: launchAt,
            op: 'flight',
            priority,
            from,
            to,
            effect: projectileEffect(event.projectile),
            sizeTier: event.sizeTier,
            flightMs,
          });
          plan.push({ atMs: strike, op: 'float', target: event.target, damage: event.damage, priority });
          // Each skewered extra floats as the serpent reaches it along the ray.
          for (const target of event.pierced) {
            const at = resolve(event.to, target);
            plan.push({
              atMs: launchAt + flightMs * rayFraction(from, to, at),
              op: 'float',
              target,
              damage: event.damage,
              priority,
            });
          }
          break;
        }
        lastImpactAt = launchAt + flightMs;
        if (event.kind === 'projectile') lastRootImpactAt = lastImpactAt;
        // A fire-mage chain hop always flies as a bare magic arrow (PRD #419
        // slice F), whatever the primary carries: smaller arrows leaping off the
        // fireball make the cascade - and its damage decay - readable. The
        // archer's Ricochet (ADR-0237) is a real arrow/bolt bounce, so it keeps
        // its own physical look (never the magic-arrow puff) and its size tier.
        const isArcherFlight = event.projectile === 'bow-arrow' || event.projectile === 'bolt';
        const chainMagic = event.kind === 'chain' && !isArcherFlight;
        plan.push({
          atMs: launchAt,
          op: 'flight',
          priority,
          from,
          to,
          effect: chainMagic ? 'magic-arrow' : projectileEffect(event.projectile),
          sizeTier: chainMagic ? 0 : event.sizeTier,
          flightMs,
          // Home onto the struck enemy so the tracer lands on it as it moves (ADR-0216).
          target: event.target,
        });
        plan.push({
          atMs: lastImpactAt,
          op: 'float',
          target: event.target,
          damage: event.damage,
          priority,
          // The struck-effect so the caller can play a physical hit thwack
          // (archer arrow/bolt, ADR-0237); absent for a magic chain / pierce.
          ...(isArcherFlight ? { viaEffect: projectileEffect(event.projectile) } : {}),
        });
        // Comet (PRD #419 slice E): the sim already hurled the enemy to
        // `knockedTo`; the hurl op replays that slide on the enemy's own
        // sprite at the impact instant, strike tile -> landing tile.
        if (event.kind === 'projectile' && event.knockedTo) {
          plan.push({
            atMs: lastImpactAt,
            op: 'hurl',
            priority,
            target: event.target,
            fromTile: { ...event.to },
            toTile: { ...event.knockedTo },
            durationMs: COMET_HURL_MS,
          });
        }
        break;
      }
      case 'explosion':
      case 'flame-strike':
      case 'combustion': {
        // Combustion (PRD #419 slice C) is the same on-hit-detonation shape:
        // the mushroom cloud lands at the corpse tile at its trigger's impact
        // instant (time zero on a standalone zone-burn ground-fx message),
        // each splashed neighbor floating its share.
        plan.push({
          atMs: lastImpactAt,
          op: 'impact',
          priority,
          at: resolve(event.at),
          effect:
            event.kind === 'combustion'
              ? 'fire-mushroom'
              : event.kind === 'explosion'
                ? event.big // the Cataclysm keystone's grown blast (PRD #378)
                  ? 'big-explosion'
                  : 'explosion'
                : 'flame-strike',
          radiusTiles: event.radius,
        });
        for (const target of event.targets) {
          plan.push({ atMs: lastImpactAt, op: 'float', target, damage: event.damage, priority });
        }
        break;
      }
      case 'nova-ray': {
        // The ring launches when the volley does, each ray a beat behind the
        // last (the spiral); struck enemies float as the ray reaches them.
        const from = resolve(event.from);
        const to = resolve(event.to);
        const launchAt = event.rayIndex * NOVA_RAY_STAGGER_MS;
        const flightMs = projectileFlightMs(from, to);
        lastImpactAt = launchAt + flightMs;
        plan.push({
          atMs: launchAt,
          op: 'flight',
          priority,
          from,
          to,
          effect: projectileEffect(event.projectile),
          sizeTier: event.sizeTier,
          flightMs,
        });
        for (const target of event.targets) {
          const at = resolve(event.to, target);
          plan.push({
            atMs: launchAt + flightMs * rayFraction(from, to, at),
            op: 'float',
            target,
            damage: event.damage,
            priority,
          });
        }
        break;
      }
      case 'nova': {
        // The normal homing volley lands first; then sixteen fireballs blast
        // evenly around the caster to the edge of the circular damage reach.
        const atMs = lastRootImpactAt;
        const from = resolve(event.at);
        for (let i = 0; i < NOVA_FIREBALL_COUNT; i++) {
          const angle = (i / NOVA_FIREBALL_COUNT) * Math.PI * 2;
          const to = resolve({
            x: event.at.x + Math.cos(angle) * event.radius,
            y: event.at.y + Math.sin(angle) * event.radius,
          });
          plan.push({
            atMs,
            op: 'flight',
            priority,
            from,
            to,
            effect: 'fireball',
            sizeTier: 1,
            flightMs: projectileFlightMs(from, to),
          });
        }
        for (const target of event.targets) {
          plan.push({ atMs, op: 'float', target, damage: event.damage, priority });
        }
        lastImpactAt = atMs;
        break;
      }
      case 'ground-damage': {
        for (const hit of event.hits) {
          plan.push({
            atMs: lastImpactAt,
            op: 'float',
            target: hit.target,
            damage: hit.damage,
            priority,
            ground: true,
          });
        }
        break;
      }
      case 'splinter': {
        // A Splinter burst ray (PRD #419 slice D): radial shrapnel erupting
        // from the parent projectile's struck tile at the PARENT's impact
        // instant, each ray a spiral beat behind by its rayIndex (the nova
        // stagger). Always the magic-arrow art at size 0 - visibly weaker than
        // whatever the primary flew as (the PRD's shrapnel rule). Struck
        // enemies float as the ray reaches them, and any cascade the hit
        // spawned (emitted right after this event) triggers off the ray's own
        // landing via lastImpactAt, like a nova ray's.
        const from = resolve(event.from);
        const to = resolve(event.to);
        const launchAt = lastRootImpactAt + event.rayIndex * NOVA_RAY_STAGGER_MS;
        const flightMs = projectileFlightMs(from, to);
        lastImpactAt = launchAt + flightMs;
        plan.push({
          atMs: launchAt,
          op: 'flight',
          priority,
          from,
          to,
          effect: 'magic-arrow',
          sizeTier: 0,
          flightMs,
        });
        for (const target of event.targets) {
          const at = resolve(event.to, target);
          plan.push({
            atMs: launchAt + flightMs * rayFraction(from, to, at),
            op: 'float',
            target,
            damage: event.damage,
            priority,
          });
        }
        break;
      }
      case 'telegraph': {
        // The delayed-hit warning (PRD #378 story 14): sparkle at the doomed
        // tile from the proc instant for the whole fall.
        plan.push({
          atMs: lastImpactAt,
          op: 'ground',
          priority,
          at: resolve(event.at),
          effect: 'sparkle',
          durationMs: event.durationMs,
        });
        break;
      }
      case 'meteor': {
        // The meteor carries its OWN delay (its telegraph rode the proccing
        // volley's message): the star falls from above the tile the moment its
        // impact message arrives, detonating - big blast + floats - at the
        // fall's end, never off a preceding flight's landing.
        const at = resolve(event.at);
        plan.push({
          atMs: 0,
          op: 'flight',
          priority,
          from: { x: at.x, y: at.y - METEOR_DROP_PX },
          to: at,
          effect: 'star',
          sizeTier: 0,
          flightMs: METEOR_FALL_MS,
        });
        plan.push({
          atMs: METEOR_FALL_MS,
          op: 'impact',
          priority,
          at,
          effect: 'big-explosion',
          radiusTiles: event.radius,
        });
        for (const target of event.targets) {
          plan.push({
            atMs: METEOR_FALL_MS,
            op: 'float',
            target,
            damage: event.damage,
            priority,
          });
        }
        // The detonation is this beat's landing instant, so the molten pool
        // riding the same message (PRD #419 slice B) ignites at the boom, not
        // while the star is still falling.
        lastImpactAt = METEOR_FALL_MS;
        break;
      }
      case 'vortex-summon': {
        // The materialize/dissipate swirl (PRD #378): one-shot at the tile.
        plan.push({
          atMs: lastImpactAt,
          op: 'impact',
          priority,
          at: resolve(event.at),
          effect: 'death-vortex',
          radiusTiles: 1,
        });
        break;
      }
      case 'fire-field': {
        // Ground ignites at the triggering hit's landing instant (a standalone
        // trail message has no preceding flight, so it ignites immediately).
        // The molten residue look (PRD #419 slice B) selects the splat op -
        // one blood static per tile, hash-picked, fire-tinted at draw - and
        // ignores `orient` (a splat has no direction). Otherwise each tile
        // loops its strip - run-line oriented when the event carries an
        // `orient` (PRD #419), parity-chosen otherwise - for the duration.
        for (const tile of event.tiles) {
          if (event.look === 'molten') {
            plan.push({
              atMs: lastImpactAt,
              op: 'ground',
              priority,
              at: resolve(tile),
              effect: 'molten',
              durationMs: event.durationMs,
              art: moltenSplatArt(tile),
            });
          } else {
            // The archer (ADR-0237) reuses the field visual: `poison` loops the
            // poison strip, `caltrop` a slow marker; else the fire strip.
            const effect: GroundEffectId =
              event.look === 'poison'
                ? poisonFieldEffect(tile, event.orient)
                : event.look === 'caltrop'
                  ? 'caltrop'
                  : fireFieldEffect(tile, event.orient);
            plan.push({
              atMs: lastImpactAt,
              op: 'ground',
              priority,
              at: resolve(tile),
              effect,
              durationMs: event.durationMs,
            });
          }
        }
        break;
      }
      case 'status': {
        // Batched cosmetic tick blips (archer bleed spurts, ADR-0237): resolve
        // each mark tile and drop one status op at the last impact instant.
        plan.push({
          atMs: lastImpactAt,
          op: 'status',
          priority,
          marks: event.marks.map((m) => ({ at: resolve({ x: m.x, y: m.y }), status: m.status })),
        });
        break;
      }
    }
  }
  return plan;
}

/**
 * Fold repeated damage numbers for the same target into one number while
 * retaining the exact total damage represented.
 *
 * Every priority folds, primary included (PRD #826). A primary float used to be
 * exempt, which made the most common Arena case - one runner landing rapid hits
 * on one enemy - the one path with no bound at all: a fresh label per hit, each
 * outliving its successor, so the numbers stacked into an unreadable column and
 * the label churn was unbounded. Folding is lossless, so the player reads an
 * accurate rolling total instead, and the window (which the presentation tier
 * widens under pressure) sets how often that total refreshes. Priority is part
 * of the fold key, so primary feedback still schedules ahead of secondary work
 * and never disappears into it; the telegraphs and projectile art that share the
 * primary class are not floats and are untouched here.
 */
export function aggregateDamageFloats(
  plan: readonly PlannedFx[],
  windowMs: number,
): PlannedFx[] {
  const result: PlannedFx[] = [];
  const latest = new Map<string, { index: number; atMs: number }>();

  for (const op of plan) {
    if (op.op !== 'float') {
      result.push(op);
      continue;
    }
    const key = `${op.target}:${op.ground === true ? 'ground' : 'body'}:${op.priority}`;
    const previous = latest.get(key);
    if (previous && op.atMs - previous.atMs <= windowMs) {
      const prior = result[previous.index];
      if (prior?.op === 'float') {
        result[previous.index] = { ...prior, damage: prior.damage + op.damage };
        continue;
      }
    }
    latest.set(key, { index: result.length, atMs: op.atMs });
    result.push({ ...op });
  }

  return result;
}

/**
 * Order planned work for admission into the bounded scheduler while retaining
 * source order within each priority class.
 */
export function prioritizePlannedFx(plan: readonly PlannedFx[]): PlannedFx[] {
  const rank = { primary: 0, secondary: 1, decorative: 2 } as const;
  return plan
    .map((op, index) => ({ op, index }))
    .sort((a, b) => rank[a.op.priority] - rank[b.op.priority] || a.index - b.index)
    .map(({ op }) => op);
}
